/* eslint-disable @typescript-eslint/naming-convention */
import { cloneDeep, difference, isBoolean, isNumber, isString } from 'lodash';
import { getRotatePosition } from '@/utils/collision-kit';
import { traverseTree, traverseTrees } from '@/utils/tree-kit';
import { LayerType, type IGroupExtension, type IGroupLayerProps, type ILayerProps, type IPoint } from '@/version-types';
import { getGroupExtension, resetGroupLayerRotate, type IService } from '../../shared';
import type { ServiceManager } from '../service-manager';

/** 图层变化事件 */
const changeEventName = 'edgeless:flat-layer:1';
/** 图层变化事件（带图层ID） */
const changeByIdEventName = (layerId: string) => `${changeEventName}:${layerId}`;
/** 图层删除事件 */
const deleteEventName = 'edgeless:flat-layer:2';
/** 舞台尺寸变化事件 */
const resizeEventName = 'edgeless:flat-layer:3';
/**
 * 图层数据的扁平化存储
 */
export class FlatLayerService implements IService {
    /** 图层数据 */
    private __data: Record<string, ILayerProps> = {};
    /** 正在编辑的ID */
    private __editId: string = '';
    /** 删除图层协作操作类型的编辑版本 */
    private __delLayerRealEditVersion: Record<string, Record<string, [number, string]>> = {};

    constructor(readonly service: ServiceManager) {}

    onInit() {
        this.__data = {};
        this.__editId = '';
        this.__delLayerRealEditVersion = {};
    }

    onFrameResize(fn: (frameId: number) => void, weight = 0) {
        return this.service.events.on(resizeEventName, fn, weight);
    }

    __emitFrameResize(frameId: number) {
        this.service.events.emit(resizeEventName, frameId);
    }

    __emitChange(
        layer: ILayerProps | null,
        onlyUpdateBaseInfo: boolean,
        args: { isAdd?: boolean; isDelete?: boolean; deleteIds?: string[]; dirtyFrameId?: number },
    ) {
        // 增删改事件
        this.service.events.emit(changeEventName, layer, onlyUpdateBaseInfo, args);

        // 增改事件
        if (layer) {
            this.service.events.emit(changeByIdEventName(layer.id), layer, onlyUpdateBaseInfo, args);
        }

        // 删除事件
        if (args.isDelete && args.deleteIds) {
            this.service.events.emit(deleteEventName, args.deleteIds, args.dirtyFrameId);
        }
    }

    /**
     * 监听图层变化事件
     * @param fn - 事件处理函数
     * @param layerId - 可选；如果指定则只监听该图层的变化事件
     * @param weight - 事件权重
     * @returns - 事件监听器
     */
    onChange(
        fn: (
            layer: ILayerProps | null,
            onlyUpdateBaseInfo: boolean,
            args: { isAdd?: boolean; isDelete?: boolean; deleteIds?: string[]; dirtyFrameId?: number },
        ) => void,
        layerId?: string,
        weight = 0,
    ) {
        if (layerId) {
            return this.service.events.on(changeByIdEventName(layerId), fn, weight);
        }

        return this.service.events.on(changeEventName, fn, weight);
    }

    /**
     * 监听图层删除事件
     * @param fn - 事件处理函数
     * @param weight - 事件权重
     * @returns - 事件监听器
     */
    onDelete(fn: (layerIds: string[], dirtyFrameId: number) => void, weight = 0) {
        return this.service.events.on(deleteEventName, fn, weight);
    }

    get data() {
        return this.__data;
    }

    set data(data: Record<string, ILayerProps>) {
        this.__data = data;
    }

    get(layerId: string) {
        return this.__data[layerId];
    }

    set(
        layerId: string,
        layer: ILayerProps,
        options?: {
            /** 是否更新依赖 */
            updateDependencies?: boolean;
            /** 是否只更新基础信息 */
            onlyUpdateBaseInfo?: boolean;
        },
    ) {
        const { updateDependencies = true, onlyUpdateBaseInfo = false } = options || {};
        const isBackgroundLayer = this.checkIsBackground(layer);

        let isSizeChange = false;
        if (isBackgroundLayer) {
            const oldLayer = this.get(layerId);
            if (oldLayer && (oldLayer.width !== layer.width || oldLayer.height !== layer.height)) {
                isSizeChange = true;
            }
        }
        if (isBoolean(layer.__isOnTextEditing)) {
            if (layer.__isOnTextEditing) {
                this.__editId = layer.id;
            } else if (this.__editId === layer.id) {
                this.__editId = '';
            }
        } else if (this.__editId === layer.id) {
            layer.__isOnTextEditing = true;
        }
        let isAdd = false;
        if (!this.__data[layerId]) {
            isAdd = true;
        }
        this.__data[layerId] = layer;

        if (updateDependencies) {
            // console.log('updateDependencies', { layer });
            const groupId = layer.parentId || (this.checkIsGroup(layer) ? layer.id : null);
            if (groupId) {
                // 重置父图层的大小并更新引用
                const updateParent = (id: string) => {
                    const layer = this.get(id);
                    if (!layer) return;
                    const positions: IPoint[] = [];
                    (layer.children || []).forEach((e: ILayerProps) => {
                        const item = this.get(e.id);
                        if (!item) return;
                        const position = getRotatePosition(item);
                        positions.push(...position);
                    });
                    if (!positions.length) return;
                    const newLayer = { ...layer };
                    resetGroupLayerRotate(newLayer, positions);
                    this.__data[id] = newLayer;
                    // 更新父图层中的子图层引用
                    if (newLayer.parentId) {
                        const parentLayer = this.get(newLayer.parentId);
                        if (parentLayer && parentLayer.children) {
                            const index = parentLayer.children.findIndex((child) => child.id === id);
                            if (index !== -1) {
                                parentLayer.children[index] = newLayer;
                            }
                        }
                        // 递归更新上层组
                        updateParent(newLayer.parentId);
                    }
                };
                const layer = this.get(groupId);
                // 找出最底层叶子结点
                const parentInfo: Map<string, boolean> = new Map();
                traverseTree(layer, (node) => {
                    if (!node) return;
                    if (!node.children || node.children.length === 0) {
                        if (node.parentId) {
                            if (!parentInfo.has(node.parentId)) {
                                parentInfo.set(node.parentId, true);
                            }
                        }
                    } else {
                        if (node.parentId) {
                            parentInfo.set(node.parentId, false);
                        }
                    }
                });
                for (const [key, value] of parentInfo.entries()) {
                    if (value) {
                        // 更新父图层
                        updateParent(key);
                    }
                }
                // 更新所有子图层的引用
                const updateChildrenRefs = (parentLayer: ILayerProps) => {
                    if (!parentLayer.children) return;
                    parentLayer.children = parentLayer.children.map((child) => {
                        const updatedChild = this.__data[child.id];
                        if (updatedChild.children) {
                            updateChildrenRefs(updatedChild);
                        }
                        return updatedChild;
                    });
                };
                // 从根节点开始更新引用
                const rootLayer = this.getWrapLayer(layerId, true);
                if (rootLayer) {
                    updateChildrenRefs(rootLayer);
                }
            }
        }

        this.__emitChange(layer, onlyUpdateBaseInfo, { isAdd, isDelete: false });

        if (isSizeChange && isNumber(layer.__originIndex)) {
            this.__emitFrameResize(layer.__originIndex);
        }

        // console.log('set====>', { data: cloneDeep(this.__data) });
    }

    delete(layerId: string) {
        const layer = this.__data[layerId];
        const deleteIds: Set<string> = new Set();
        deleteIds.add(layerId);
        const dirtyFrameId = layer.__originIndex;
        if (layer && !this.checkIsExtraGroup(layer)) {
            traverseTree(layer, (node) => {
                if (node) {
                    deleteIds.add(node.id);
                }
            });
        }

        delete this.__data[layerId];
        if (layerId === this.__editId) {
            this.__editId = '';
        }

        this.__emitChange(null, false, {
            isAdd: false,
            isDelete: true,
            deleteIds: Array.from(deleteIds),
            dirtyFrameId,
        });
    }

    /** 检查图层是否是组 */
    checkIsGroup(layer: ILayerProps) {
        return layer?.layerType === LayerType.GROUP_LAYER;
    }

    /** 检查图层是否是临时组 */
    checkIsExtraGroup(layer: ILayerProps) {
        if (this.checkIsGroup(layer) && (layer.data as IGroupLayerProps).isExtraGroup) {
            return true;
        }
        return false;
    }

    /** 检查图层是否是正式组 */
    checkIsFormalGroup(layer: ILayerProps) {
        if (this.checkIsGroup(layer) && !(layer.data as IGroupLayerProps).isExtraGroup) {
            return true;
        }
        return false;
    }

    /** 检查图层是否是背景图层 */
    checkIsBackground(layer: ILayerProps) {
        return !!this.service.behavior.get(layer)?.isBackgroundLayer?.();
    }

    /** 检查图层是否是主图层 */
    checkIsMain(layer: ILayerProps) {
        return !!this.service.behavior.get(layer)?.isMainLayer?.();
    }

    /** 检查图层是否包含 Agent 图层 */
    checkHasAgent(layer?: ILayerProps | null) {
        if (!layer) return false;
        if (layer.extraInfo?.agent) return true;
        return !!layer.children?.some((child) => this.checkHasAgent(child));
    }

    /** 检查图层是否为变形文字 */
    checkIsTextTransform(layer?: ILayerProps) {
        if (!layer) return false;
        return this.service.behavior.get(layer)?.checkIsTextTransform?.(layer);
    }

    /** 检查图层是否为 AI 花字 */
    checkIsAiText(layer?: ILayerProps) {
        if (!layer) return false;
        return this.service.behavior.get(layer)?.checkIsAiText?.(layer);
    }

    /** 检查图层是否为照片图层 */
    checkIsPhoto(layer?: ILayerProps) {
        if (!layer) return false;
        return this.service.behavior.get(layer)?.checkIsPhoto?.(layer);
    }

    /** 检查图层是否需要隐藏 */
    private checkIsHidden(layer: ILayerProps) {
        return !!layer.hidden || !!layer.__isHidden;
    }

    /** 设置图层隐藏 */
    setHidden(layer: ILayerProps, hidden: boolean) {
        layer.hidden = hidden;
        delete layer.__isHidden;
    }

    /** 检查图层是否是脏数据 */
    checkLayerIsDirty({
        originLayer,
        latestIsExtraGroup = false,
        latestIsFormalGroup = false,
    }: {
        /** 原始图层 */
        originLayer: ILayerProps;
        /** 最新图层是否是临时组 */
        latestIsExtraGroup?: boolean;
        /** 最新图层是否是正式组 */
        latestIsFormalGroup?: boolean;
    }) {
        let isDirty = false;
        const checkLayersParentIsDirty = (layers: ILayerProps[]) => {
            let isDirty = false;
            traverseTrees(layers, (node) => {
                const oldParentId = node.parentId || '';
                const layer = this.getLayer(node.id);
                const newParentId = layer?.parentId || '';
                if (!layer || oldParentId !== newParentId) {
                    isDirty = true;
                }
            });
            return isDirty;
        };
        const latestLayer = this.getLayer(originLayer.id);
        const waitCheckLayers: ILayerProps[] = [];

        // 检查最新数据是否为临时组
        if (latestIsExtraGroup) {
            if (latestLayer) {
                if (!this.checkIsExtraGroup(latestLayer)) {
                    isDirty = true;
                } else {
                    waitCheckLayers.push(originLayer);
                }
            } else {
                originLayer.children?.forEach((item) => {
                    const layer = this.getLayer(item.id);
                    if (!layer) {
                        isDirty = true;
                    }
                    if (!isDirty) {
                        const parentId = layer?.parentId || '';
                        if (parentId && !this.checkIsExtraGroup(this.getLayer(parentId))) {
                            isDirty = true;
                        }
                    }
                    if (!isDirty && item.children) {
                        waitCheckLayers.push(...item.children);
                    }
                });
            }
            if (!isDirty) {
                isDirty = checkLayersParentIsDirty(waitCheckLayers);
            }
            return isDirty;
        }

        // 检查最新数据是否为正式组
        if (!latestLayer || (latestIsFormalGroup && !this.checkIsFormalGroup(latestLayer))) {
            isDirty = true;
        } else {
            // 检查图层是否一致
            const oldIds: string[] = [];
            const newIds: string[] = [];
            traverseTree(originLayer, (node) => {
                oldIds.push(node.id);
            });
            traverseTree(latestLayer, (node) => {
                newIds.push(node.id);
            });
            const diff = difference(oldIds, newIds);
            if (diff.length) {
                isDirty = true;
            }
            waitCheckLayers.push(originLayer);
        }

        if (!isDirty) {
            isDirty = checkLayersParentIsDirty(waitCheckLayers);
        }

        return isDirty;
    }

    /** 检查图层是否相等 */
    checkLayerEqual(originLayer: ILayerProps, latestLayer: ILayerProps) {
        if (!originLayer || !latestLayer) return false;
        const originParentId = originLayer.parentId || '';
        const latestParentId = latestLayer.parentId || '';
        if (originParentId !== latestParentId) return false;
        const originIds: string[] = [];
        const latestIds: string[] = [];
        traverseTree(originLayer, (node) => {
            originIds.push(node.id);
        });
        traverseTree(latestLayer, (node) => {
            latestIds.push(node.id);
        });
        return difference(originIds, latestIds).length === 0;
    }

    /**
     * 检查图层是否可以拆分
     *
     * @param layer - 图层属性，会递归检查父级
     */
    checkPossibleSplit(layer: ILayerProps) {
        let possibleUnpack = false;
        let stop = false;

        const check = (layer: ILayerProps) => {
            if (layer) {
                const extension = this.getGroupExtension<IGroupExtension>(layer);
                if (extension) {
                    possibleUnpack = !!this.service.behavior.get(layer)?.possibleUnpack?.(layer);
                    stop = true;
                }
            }
            if (!stop && !possibleUnpack && layer?.parentId) {
                check(this.getLayer(layer.parentId));
            }
        };

        check(layer);

        if (stop) {
            return possibleUnpack;
        }

        return true;
    }

    /**
     * 检查是否可以剪切
     *
     * @param layer - 图层属性
     */
    checkPossibleCut(layer: ILayerProps) {
        // 如果剪切的是拓展组的组内元素，并且组内元素不能拆分，则不进行剪切
        if (this.checkWrapIsGroupExtension(layer)) {
            if (layer?.parentId && !this.checkPossibleSplit(layer)) {
                return false;
            }
        }
        return true;
    }

    /**
     * 检查是否可以删除
     *
     * @param layer - 图层属性
     */
    checkPossibleDelete(layer: ILayerProps) {
        return this.checkPossibleCut(layer);
    }

    /** 检查图层是否禁用操作 */
    checkDisableChildBaseOperate(layer?: ILayerProps | null) {
        if (!layer) return false;

        let disableOperate = false;
        let stop = false;

        const check = (layer: ILayerProps) => {
            if (layer) {
                const checkDisableChildBaseOperate = this.service.behavior.get(layer)?.checkDisableChildBaseOperate;
                if (checkDisableChildBaseOperate) {
                    disableOperate = checkDisableChildBaseOperate(layer);
                    stop = true;
                }
            }
            if (!stop && !disableOperate && layer?.parentId) {
                check(this.getLayer(layer.parentId));
            }
        };

        check(layer);

        if (stop) {
            return disableOperate;
        }

        return false;
    }

    /**
     * 检查是否可以解组
     *
     * @param layer - 图层属性
     */
    checkBrokenGroup(layer: ILayerProps | null) {
        if (!layer) return false;
        // 判断拓展图层是否可以拆分
        const extension = this.getGroupExtension<IGroupExtension>(layer);
        if (extension) {
            return !!this.service.behavior.get(layer)?.possibleUnpack?.(layer);
        }
        // 判断是否是vip花字图层，如果是则不能拆分
        return !this.checkIsTextMaterialVip(layer);
    }

    /** 检查图层是否是vip花字图层 */
    checkIsTextMaterialVip(layer?: ILayerProps | null) {
        if (!layer) return false;
        return (layer.data as IGroupLayerProps)?.isTextMaterial && (layer.data as IGroupLayerProps)?.isVip;
    }

    /**
     * 检查图层是否是拓展图层
     *
     * @param layer - 图层属性
     */
    checkIsGroupExtension(layer?: ILayerProps | null) {
        if (!layer) return false;
        const extension = this.getGroupExtension<IGroupExtension>(layer);
        return !!extension;
    }

    /** 检查外壳图层是否是拓展图层 */
    checkWrapIsGroupExtension(layer?: ILayerProps | null) {
        if (!layer) return false;
        if (this.checkIsGroupExtension(layer)) return true;
        if (layer.parentId) {
            const parent = this.getLayer(layer.parentId);
            if (parent) {
                return this.checkWrapIsGroupExtension(parent);
            }
        }
        return false;
    }

    /** 检查图层是否需要隐藏 */
    checkIsNeedHidden(layerId: string | ILayerProps) {
        const wrapLayer = isString(layerId) ? this.get(layerId) : layerId;
        if (!wrapLayer) return true;
        if (this.checkIsHidden(wrapLayer)) return true;

        let needHidden = false;

        const updateWrapLayer = (id: string, currentLayer: ILayerProps) => {
            const layer = this.get(id);
            if (!layer) {
                needHidden = this.checkIsHidden(currentLayer);
                return;
            }
            if (this.checkIsHidden(layer)) {
                needHidden = true;
                return;
            }
            if (layer.parentId) {
                updateWrapLayer(layer.parentId, layer);
            }
        };

        if (wrapLayer.parentId) {
            updateWrapLayer(wrapLayer.parentId, wrapLayer);
        }

        return needHidden;
    }

    /** 检查图层是否需要锁定 */
    checkIsNeedLock(layerId: string) {
        const wrapLayer = this.get(layerId);
        if (!wrapLayer) return true;
        if (wrapLayer.lock) return true;

        let needLock = false;

        const updateWrapLayer = (id: string, currentLayer: ILayerProps) => {
            const layer = this.get(id);
            if (!layer) {
                needLock = !!currentLayer.lock;
                return;
            }
            if (layer.lock) {
                needLock = true;
                return;
            }
            if (layer.parentId) {
                updateWrapLayer(layer.parentId, layer);
            }
        };

        if (wrapLayer.parentId) {
            updateWrapLayer(wrapLayer.parentId, wrapLayer);
        }

        return needLock;
    }

    /** 检查组图层是否可以调整宽高 */
    checkGroupLayerCanResize(children: ILayerProps[]) {
        let hasHorizontal = true,
            hasVertical = true;
        const traverse = (items: ILayerProps[]) => {
            for (const child of items) {
                const { horizontal, vertical } = this.service.behavior.get(child)?.whenGroupCanResize?.(child) ?? {
                    horizontal: false,
                    vertical: false,
                };
                hasHorizontal = hasHorizontal && horizontal;
                hasVertical = hasVertical && vertical;
                if (child.layerType === LayerType.GROUP_LAYER && child.children) {
                    traverse(child.children);
                }
            }
        };
        traverse(children);
        return { hasHorizontal, hasVertical };
    }

    /** 检查图层是否可以调整宽高 */
    checkCanResizeOperate(layer: ILayerProps) {
        let resizeState = {
            top: true,
            bottom: true,
            left: true,
            right: true,
        };
        if (!layer) return resizeState;
        const params = this.service.behavior.get(layer)?.onCheckCanResize?.(layer);
        if (params) {
            resizeState = {
                top: params.vertical,
                bottom: params.vertical,
                left: params.horizontal,
                right: params.horizontal,
            };
        }

        // 组的逻辑
        const children = layer?.children;
        if (children && children.length) {
            const { hasHorizontal, hasVertical } = this.checkGroupLayerCanResize(children);
            resizeState = {
                top: hasVertical,
                bottom: hasVertical,
                left: hasHorizontal,
                right: hasHorizontal,
            };
        }
        return resizeState;
    }

    /** 深度遍历图层 ID */
    traverseIds(skipExtraGroup: boolean, renderIds: string[], fn: (layerId: string) => void) {
        const flatLayer = (layer?: ILayerProps) => {
            if (!layer) return;
            const isExtraGroup = this.checkIsExtraGroup(layer);
            if (!skipExtraGroup || !isExtraGroup) {
                (layer.children || []).forEach((item) => {
                    flatLayer(item);
                });
            }
            fn(layer.id);
        };
        for (const id of renderIds) {
            flatLayer(this.getLayer(id));
        }
    }

    /**
     * 获取外壳图层
     *
     * @param id - 图层 ID
     * @param extraGroupIsWrap - 可选；临时组是否可以作为外壳；默认值为 `false`
     */
    getWrapLayer(layerId: string, extraGroupIsWrap = false) {
        let wrapLayer = this.get(layerId);
        if (!wrapLayer) return null;
        const updateWrapLayer = (id: string, currentLayer: ILayerProps) => {
            const layer = this.get(id);
            if (!layer) {
                wrapLayer = currentLayer;
                return;
            }

            if (extraGroupIsWrap) {
                if (this.checkIsExtraGroup(layer)) {
                    // wrapLayer = currentLayer;
                    wrapLayer = layer;
                    return;
                }
            }

            if (layer.parentId) {
                updateWrapLayer(layer.parentId, layer);
            } else {
                wrapLayer = layer;
            }
        };

        if (wrapLayer?.parentId) {
            updateWrapLayer(wrapLayer.parentId, wrapLayer);
        }
        // console.log('updateWrapLayer', wrapLayer);

        return wrapLayer;
    }

    /** 获取图层 */
    getLayer(layerId: string, withClone = false) {
        const layer = this.get(layerId);
        if (withClone && layer) {
            return this.getLayerWithClone(cloneDeep(layer));
        }
        return layer;
    }

    /** 转换为 Edgeless 图层数据，长图模式会有个偏移量 */
    convertLayer(layer: ILayerProps, withClone?: boolean): ILayerProps | null;
    convertLayer(layerId: string, withClone?: boolean): ILayerProps | null;
    convertLayer(layerIdOrLayer: string | ILayerProps, withClone = false): ILayerProps | null {
        const layer = isString(layerIdOrLayer) ? this.getLayer(layerIdOrLayer, withClone) : layerIdOrLayer;
        if (!layer) return null;
        const newLayer = {
            ...layer,
            top: layer.top + this.service.transform.calcLayerOffsetTop(layer),
        };
        return withClone ? this.getLayerWithClone(cloneDeep(newLayer)) : newLayer;
    }

    /** 获取子图层 */
    getChildrenLayers(layerId: string, ignoreGroup = true) {
        const layer = this.getLayer(layerId);
        if (!layer) return [];
        const layers: ILayerProps[] = [];
        traverseTree(layer, (item) => {
            if (ignoreGroup && this.checkIsGroup(item)) return;
            layers.push(item);
        });
        return layers;
    }

    getGroupExtension<T>(layer: ILayerProps): T | null {
        return getGroupExtension<T>(layer);
    }

    /** 获取拷贝的图层，并执行 onBeforeGetLayerWithClone 行为 */
    getLayerWithClone(layer: ILayerProps) {
        if (!layer) {
            return layer;
        }
        traverseTree(layer, (item) => {
            this.service.behavior.get(item)?.onBeforeGetLayerWithClone?.(item);
        });
        return layer;
    }

    /** 设置图层协作操作类型的编辑版本 */
    setRealEditTypeVersion(layerId: string, editType: string, serverTime: number) {
        const type = this.__parseEditType(editType);
        const layer = this.get(layerId);
        if (layer) {
            const renderVersion = layer?.__renderRealEditVersion || {};
            const version = renderVersion[type.key] || [0, type.value];
            if (version[0] < serverTime) {
                renderVersion[type.key] = [serverTime, type.value];
                layer.__renderRealEditVersion = renderVersion;
                // console.log('ot 写入renderVersion 1', layerId, cloneDeep(renderVersion));
                return renderVersion;
            }
        } else {
            this.__delLayerRealEditVersion[layerId] = this.__delLayerRealEditVersion[layerId] || {};
            this.__delLayerRealEditVersion[layerId][type.key] = [serverTime, type.value];
            // console.log('ot 写入renderVersion 2', layerId, cloneDeep(this.__delLayerRealEditVersion[layerId][type.key]));
        }
        return;
    }

    /** 获取图层协作操作类型的编辑版本 */
    getRealEditTypeVersion(layerId: string, editType: string): [number, string] {
        const type = this.__parseEditType(editType);
        const layer = this.get(layerId);
        if (layer) {
            const renderVersion = layer.__renderRealEditVersion?.[type.key] || [0, type.value];
            // console.log('ot 获取renderVersion 1', layerId, cloneDeep(renderVersion));
            return renderVersion;
        }
        // console.log('ot 获取renderVersion 2', layerId, cloneDeep([0, type.value]));
        return this.__delLayerRealEditVersion[layerId]?.[type.key] || [0, type.value];
    }

    private __parseEditType(editType: string) {
        const types = editType.split(':');
        return {
            key: types[1] || types[0],
            value: types[1] ? editType : '',
        };
    }
}
